/**
 * SM2国密算法工具类
 * 基于agent-console项目的实现
 */

import smCrypto from 'sm-crypto'

// 公钥缓存
let publicKeyCache = null

/**
 * 验证验证码格式
 * @param {string} captcha - 验证码
 * @returns {boolean} 验证码格式是否正确
 */
export function validateCaptchaFormat(captcha) {
  return captcha && captcha.length === 5 && typeof captcha === 'string'
}

/**
 * 获取公钥
 * @returns {Promise<string>} 公钥
 */
export async function getPublicKey() {
  // 如果已经有缓存的公钥，直接返回
  if (publicKeyCache) {
    return publicKeyCache
  }
  
  try {
    // 发送请求获取公钥
    const baseUrl = process.env.VUE_APP_API_BASE_URL || '/api'
    const response = await fetch(`${baseUrl}/user/pub-config`)
    
    if (!response.ok) {
      console.warn(`获取公钥失败: HTTP ${response.status} ${response.statusText}，使用默认公钥`)
      throw new Error('HTTP错误')
    }
    
    const data = await response.json()
    
    // 尝试多种公钥字段格式
            let publicKey = null
            
            // 格式1: {code: 0, data: {sm2PublicKey: "..."}} - 后端实际格式
            if (data.code === 0 && data.data && data.data.sm2PublicKey) {
              publicKey = data.data.sm2PublicKey
            }
            // 格式2: {sm2PublicKey: "..."}
            else if (data.data && data.data.sm2PublicKey) {
              publicKey = data.data.sm2PublicKey
            }
            // 格式3: {code: 0, data: {publicKey: "..."}}
            else if (data.code === 0 && data.data && data.data.publicKey) {
              publicKey = data.data.publicKey
            }
            // 格式4: {sm2PublicKey: "..."}
            else if (data.sm2PublicKey) {
              publicKey = data.sm2PublicKey
            }
            // 格式5: {publicKey: "..."}
            else if (data.publicKey) {
              publicKey = data.publicKey
            }
            // 格式6: {code: 0, sm2PublicKey: "..."}
            else if (data.code === 0 && data.sm2PublicKey) {
              publicKey = data.sm2PublicKey
            }
            // 格式7: {code: 0, publicKey: "..."}
            else if (data.code === 0 && data.publicKey) {
              publicKey = data.publicKey
            }
    
    if (publicKey) {
      // 缓存公钥
      publicKeyCache = publicKey
      console.log('获取公钥成功:', publicKey)
      return publicKeyCache
    } else {
      console.warn('后端响应格式不符合预期，使用默认公钥:', data)
      throw new Error('响应格式错误')
    }
  } catch (error) {
    console.warn('获取公钥失败，使用默认测试公钥:', error.message)
    
    // 如果获取公钥失败，使用默认公钥（仅供测试使用）
    const defaultPublicKey = '04d8fbb6b4c6e1c8a5a9c8b1a2d3e4f5a6b7c8d9e0f1a2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c2d3e4f5a6b7c8d9e0f'
    publicKeyCache = defaultPublicKey
    return defaultPublicKey
  }
}

/**
 * 只使用SM2加密验证码
 * @param {string} captcha - 验证码
 * @returns {Promise<string>} 加密后的字符串
 */
export async function sm2EncryptCaptcha(captcha) {
  try {
    // 验证输入参数
    if (!captcha) {
      throw new Error('验证码不能为空')
    }
    if (!validateCaptchaFormat(captcha)) {
      throw new Error('验证码格式不正确，必须是5位字符')
    }
    
    // 获取公钥
    const publicKey = await getPublicKey()
    console.log('获取到公钥:', publicKey)
    
    console.log('待加密验证码:', captcha)
    
    // 使用SM2加密验证码
    const sm2 = smCrypto.sm2
    const encrypted = sm2.doEncrypt(captcha, publicKey, 1)
    
    // 转换为十六进制格式（与后端保持一致，添加04前缀）
    const result = "04" + encrypted
    
    console.log('SM2加密验证码成功:', result)
    return result
    
  } catch (error) {
    console.error('SM2加密验证码错误:', error)
    throw error
  }
}

/**
 * 使用SM2加密密码和验证码
 * @param {string} password - 密码
 * @param {string} captcha - 验证码
 * @returns {Promise<string>} 加密后的字符串
 */
export async function sm2EncryptPasswordWithCaptcha(password, captcha) {
  try {
    // 验证输入参数
    if (!password) {
      throw new Error('密码不能为空')
    }
    if (!captcha) {
      throw new Error('验证码不能为空')
    }
    if (!validateCaptchaFormat(captcha)) {
      throw new Error('验证码格式不正确，必须是5位字符')
    }
    
    // 获取公钥
    const publicKey = await getPublicKey()
    console.log('获取到公钥:', publicKey)
    
    // 合并密码和验证码
    const combinedData = captcha + password
    console.log('待加密数据:', combinedData)
    
    // 使用SM2加密，添加04前缀表示未压缩公钥
    const sm2 = smCrypto.sm2
    const encrypted = sm2.doEncrypt(combinedData, publicKey, 1)
    
    // 转换为十六进制格式（与后端保持一致，添加04前缀）
    const result = "04" + encrypted
    
    console.log('SM2加密成功:', result)
    return result
    
  } catch (error) {
    console.error('SM2加密错误:', error)
    throw error
  }
}

/**
 * SM2加密工具类
 */
export const Sm2Crypto = {
  /**
   * 验证验证码格式
   * @param {string} captcha - 验证码
   * @returns {boolean} 验证码格式是否正确
   */
  validateCaptchaFormat,
  
  /**
   * 获取公钥
   * @returns {Promise<string>} 公钥
   */
  getPublicKey,
  
  /**
   * 只使用SM2加密验证码
   * @param {string} captcha - 验证码
   * @returns {Promise<string>} 加密后的字符串
   */
  encryptCaptcha: sm2EncryptCaptcha,
  
  /**
   * 使用SM2加密密码和验证码
   * @param {string} password - 密码
   * @param {string} captcha - 验证码
   * @returns {Promise<string>} 加密后的字符串
   */
  encryptPasswordWithCaptcha: sm2EncryptPasswordWithCaptcha
}